import React from "react"

import { MDXProvider } from "@mdx-js/react"

import { Script, Link } from "gatsby"

// Temporary rollback until we can figure out how to get the i18n links working, issue @ https://github.com/zestia/gatsby/issues/841
// import { Link } from "@zestia/gatsby-plugin-react-i18next"

import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import {
  TableOfContents,
  TableOfContentsLink,
} from "@/components/TableOfContents"
import BlogVimeoVideo from "@/components/BlogVimeoVideo"
import Video from "@/components/Video"
import FeedbackMessage from "@/components/FeedbackMessage"
import { ActionSteps, ActionStep } from "@/components/ActionSteps"
import Blockquote from "@/components/Blockquote"
import Postquote from "@/components/Postquote"
import EmailTemplate from "@/components/EmailTemplate"
import MediaWrapper from "@/components/MediaWrapper"
import QuickbooksUpsell from "@/components/QuickbooksUpsell"
import { Checklist, ChecklistItem } from "@/components/Checklist"
import Longform from "@/components/Longform"
import CallToActionBanner from "@/components/CallToActionBanner"
import isExternalLink from "@/utils/is-external-link"
import SupportImage from "@/components/SupportImage"
import {
  ContentSwitcher,
  ContentSwitcherTab,
} from "@/components/ContentSwitcher"
import WistiaVideoEmbed from "@/components/WistiaVideoEmbed"
import UserInterfaceIcon from "@/components/UserInterfaceIcon"
import PlanType from "@/components/PlanType"
import Supported2faApps from "@/components/Supported2faApps"
import SupportedBrowsers from "@/components/SupportedBrowsers"
import SupportVideo from "@/components/SupportVideo"
import BlogVideo from "@/components/BlogVideo"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"
import GuidedZaps from "@/components/GuidedZaps"
import IntegrationsShowcase from "@/components/IntegrationsShowcase"
import HeadingAnchor from "@/components/HeadingAnchor"
import ScrollSpy from "@/components/ScrollSpy"
import SocialShare from "@/components/SocialShare"
import CapsuleTag from "@/components/CapsuleTag"
import TableWrapper from "@/components/TableWrapper"
import { SideMenu, SideMenuLink } from "@/components/SideMenu"
import LinkableHeading from "@/components/LinkableHeading"
import {
  InlineCTA,
  InlineCTAUI,
  InlineCTAGraphic,
} from "@/components/InlineCTA"
import RecommendedReading from "@/components/RecommendedReading"
import UpdateBanner from "@/components/UpdateBanner"

const MDXContentWrapper = ({ overrides = {}, children }) => {
  const componentsThatAllPagesCanUseWithoutImporting = {
    Link,
    Accordion,
    AccordionItem,
    TableOfContents,
    TableOfContentsLink,
    BlogVimeoVideo,
    Video,
    FeedbackMessage,
    ActionSteps,
    ActionStep,
    Blockquote,
    Postquote,
    EmailTemplate,
    MediaWrapper,
    QuickbooksUpsell,
    Checklist,
    ChecklistItem,
    Longform,
    CallToActionBanner,
    SupportImage,
    ContentSwitcher,
    ContentSwitcherTab,
    WistiaVideoEmbed,
    UserInterfaceIcon,
    PlanType,
    Supported2faApps,
    SupportedBrowsers,
    SupportVideo,
    BlogVideo,
    TryFreeCallToAction,
    GuidedZaps,
    Script,
    HeadingAnchor,
    ScrollSpy,
    SocialShare,
    SideMenu,
    SideMenuLink,
    CapsuleTag,
    TableWrapper,
    InlineCTA,
    InlineCTAUI,
    InlineCTAGraphic,
    RecommendedReading,
    UpdateBanner,
    IntegrationsShowcase,
  }

  // Use the <Link> component for all anchor tags that point to internal links
  // so that we get localised links and gatsby clientside navigation without blogpost
  // authors needing to use a component.
  const overrideHtmlElements = {
    a: props => {
      if (isExternalLink(props.href) || props.href.startsWith("#")) {
        return <a {...props} />
      }

      return <Link {...props} to={props.href} href={null} />
    },
    h2: props => {
      return <LinkableHeading level={2} {...props} />
    },
    h3: props => {
      return <LinkableHeading level={3} {...props} />
    },
  }

  return (
    <MDXProvider
      components={{
        ...componentsThatAllPagesCanUseWithoutImporting,
        ...overrideHtmlElements,
        ...overrides,
      }}
    >
      {children}
    </MDXProvider>
  )
}

export default MDXContentWrapper
